import { z } from "zod";

import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";

import {
	CompanyEnrich,
	CompanyEnrichPreviewResponse,
	CompanyEnrichPreviewResponseSchema,
	ResolvePositionsRequest,
	ResolvePositionsResponse,
	ResolvePositionsResponseSchema,
	CompanyEnrichResponse,
	CompanyEnrichResponseSchema,
	CompanyEnrichSchema,
	CompanyEnrichCompanyDetail,
	CompanyEnrichCompanyDetailSchema,
	CompanyEnrichPersonDetail,
	CompanyEnrichPersonDetailSchema,
	CreateCompanyEnrich,
	CompanySearchPreviewInput,
	CompanySearchPreviewResponse,
	CompanySearchPreviewResponseSchema,
	GeoRegionResponse,
	GeoRegionResponseSchema,
	GeoCountryResponse,
	GeoCountryResponseSchema,
	GeoStateResponse,
	GeoStateResponseSchema,
	GeoCityResponse,
	GeoCityResponseSchema,
	Industry,
	IndustrySchema,
	CompanyAutoCompleteInfo,
	CompanyAutoCompleteInfoSchema,
} from "../../pages/CompanyEnrich/schema/companyEnrich";

export const companyEnrichApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getCompanyEnrichConfigs: builder.query<CompanyEnrichResponse, RequestPaginationParams>({
			query: ({ sorting, ...params }) => ({
				url: "company-enrich",
				params: {
					...(params && { ...params }),
					...(sorting && { ...sorting }),
				},
				responseSchema: CompanyEnrichResponseSchema,
			}),
			providesTags: ["CompanyEnrich"],
		}),
		createCompanyEnrich: builder.mutation<CompanyEnrich, CreateCompanyEnrich>({
			query: (body) => ({
				url: "company-enrich",
				method: "POST",
				body,
				responseSchema: CompanyEnrichSchema,
			}),
			invalidatesTags: (_result, _error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "CompanyEnrich"];
				}

				return ["CompanyEnrich"];
			},
		}),
		updateCompanyEnrich: builder.mutation<
			Partial<CompanyEnrich>,
			Partial<CompanyEnrich> & { listId?: number }
		>({
			query: ({ id, ...body }) => ({
				url: `company-enrich/${id}`,
				method: "PATCH",
				body,
				responseSchema: CompanyEnrichSchema,
			}),
			invalidatesTags: (_result, _error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "CompanyEnrich"];
				}

				return ["CompanyEnrich"];
			},
		}),
		deleteCompanyEnrich: builder.mutation<{ id: number }, { id: number; listId?: number }>({
			query: ({ id }) => ({
				url: `company-enrich/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: (_result, _error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "CompanyEnrich"];
				}

				return ["CompanyEnrich"];
			},
		}),
		resolvePositions: builder.mutation<ResolvePositionsResponse, ResolvePositionsRequest>({
			query: (body) => ({
				url: "company-enrich/lookup/positions/resolve",
				method: "POST",
				body,
				responseSchema: ResolvePositionsResponseSchema,
			}),
		}),
		previewCompanyEnrich: builder.mutation<CompanyEnrichPreviewResponse, CreateCompanyEnrich>({
			query: (body) => ({
				url: "company-enrich/people/search/preview",
				method: "POST",
				body,
				responseSchema: CompanyEnrichPreviewResponseSchema,
			}),
		}),
		previewCompanySearch: builder.mutation<CompanySearchPreviewResponse, CompanySearchPreviewInput>(
			{
				query: (body) => ({
					url: "company-enrich/companies/search/preview",
					method: "POST",
					body,
					responseSchema: CompanySearchPreviewResponseSchema,
				}),
			}
		),
		// Lookup endpoints
		getGeoRegions: builder.query<GeoRegionResponse, void>({
			query: () => ({
				url: "company-enrich/lookup/geo-regions",
				responseSchema: GeoRegionResponseSchema,
			}),
		}),
		getGeoCountries: builder.mutation<GeoCountryResponse, { query?: string; page?: number }>({
			query: (body) => ({
				url: "company-enrich/lookup/geo-countries",
				method: "POST",
				body,
				responseSchema: GeoCountryResponseSchema,
			}),
		}),
		getGeoStates: builder.mutation<
			GeoStateResponse,
			{ countryCodes?: string[]; query?: string; page?: number }
		>({
			query: (body) => ({
				url: "company-enrich/lookup/geo-states",
				method: "POST",
				body,
				responseSchema: GeoStateResponseSchema,
			}),
		}),
		getGeoCities: builder.mutation<
			GeoCityResponse,
			{ countryCodes?: string[]; query?: string; page?: number }
		>({
			query: (body) => ({
				url: "company-enrich/lookup/geo-cities",
				method: "POST",
				body,
				responseSchema: GeoCityResponseSchema,
			}),
		}),
		getIndustries: builder.query<Industry[], void>({
			query: () => ({
				url: "company-enrich/lookup/industries",
				responseSchema: IndustrySchema.array(),
			}),
		}),
		getKeywords: builder.query<string[], { query: string }>({
			query: (params) => ({
				url: "company-enrich/lookup/keywords",
				params,
				responseSchema: z.array(z.string()),
			}),
		}),
		getTechnologies: builder.query<string[], { query: string }>({
			query: (params) => ({
				url: "company-enrich/lookup/technologies",
				params,
				responseSchema: z.array(z.string()),
			}),
		}),
		getPositions: builder.query<string[], { query: string }>({
			query: (params) => ({
				url: "company-enrich/lookup/positions",
				params,
				responseSchema: z.array(z.string()),
			}),
		}),
		getCompanyEnrichPerson: builder.query<CompanyEnrichPersonDetail, number>({
			query: (id) => ({
				url: `company-enrich/people/${id}`,
				responseSchema: CompanyEnrichPersonDetailSchema,
			}),
		}),
		getCompanyEnrichCompanyDetail: builder.query<CompanyEnrichCompanyDetail, string>({
			query: (id) => ({
				url: `company-enrich/companies/${id}/detail`,
				responseSchema: CompanyEnrichCompanyDetailSchema,
			}),
		}),
		getCompaniesAutocomplete: builder.query<CompanyAutoCompleteInfo[], { query: string }>({
			query: (params) => ({
				url: "company-enrich/lookup/companies-autocomplete",
				params,
				responseSchema: CompanyAutoCompleteInfoSchema.array(),
			}),
		}),
	}),
});

export const {
	useGetCompanyEnrichConfigsQuery,
	useCreateCompanyEnrichMutation,
	useUpdateCompanyEnrichMutation,
	useDeleteCompanyEnrichMutation,
	usePreviewCompanyEnrichMutation,
	usePreviewCompanySearchMutation,
	useGetGeoRegionsQuery,
	useGetGeoCountriesMutation,
	useGetGeoStatesMutation,
	useGetGeoCitiesMutation,
	useGetIndustriesQuery,
	useLazyGetKeywordsQuery,
	useLazyGetTechnologiesQuery,
	useLazyGetPositionsQuery,
	useResolvePositionsMutation,
	useLazyGetCompanyEnrichPersonQuery,
	useLazyGetCompanyEnrichCompanyDetailQuery,
	useLazyGetCompaniesAutocompleteQuery,
} = companyEnrichApi;
export const companyEnrichApiReducer = companyEnrichApi.reducer;
export const companyEnrichApiMiddleware = companyEnrichApi.middleware;
